import { RemoveMemberSuccessDescription } from "@components/remove-member-success-description";
import { RemoveFromProjectDialog } from "@components/project/remove-from-project-dialog";
import { DeleteIcon, Dialog, MenuItem, Text, useToast } from "@flat-ui";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/dialog-constants";
import { UserDisplay } from "@components/common/user-display";
import { useRefreshSeatUsage } from "@components/table/members/assign-seats/use-refresh-seat-usage";
import { fetchingMembersFlagsSelector } from "@store/members/members-selector";
import { removeMemberFromCompany } from "@store/members/members-slice-thunk";
import { selectedSdbCompanyNameSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { TableType, WorkspaceMemberEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";
import { FaroCoreApiUtils } from "@stellar/core-api-client";

/** Determines from where a member should be removed */

/** Where the member is removed from, as tracked */
const LOCATION = "project-member-menu";
/** Menu button for removing a member from a project or workspace */
export function ProjectRemoveMenuButton({
  companyId,
  project: selectedProject,
  member,
  onMenuClose,
  canRemoveFromProject,
  canRemoveFromWorkspace
}) {
  const [removeTarget, setRemoveTarget] = useState(null);
  const isMemberTeam = FaroCoreApiUtils.Member.isTeam(member);
  const canRemoveFromWorkspaceToo = canRemoveFromWorkspace && !isMemberTeam;
  function closeDialog() {
    setRemoveTarget(null);
    onMenuClose();
  }
  return <>
      {getMenuItem()}

      {removeTarget === "project" && <RemoveFromProjectDialog companyId={companyId} project={selectedProject} member={member} location={LOCATION} onClose={closeDialog} />}

      {removeTarget === "workspace" && <RemoveFromWorkspaceDialog companyId={companyId} member={member} onClose={closeDialog} />}
    </>;

  /** Returns the menu item, with a sub menu when the member can be removed from both */
  function getMenuItem() {
    if (canRemoveFromProject && canRemoveFromWorkspaceToo) {
      return <MenuItem label="Remove member" Icon={DeleteIcon} subMenuTrigger="click">
          <MenuItem label="From project" onClick={() => setRemoveTarget("project")} />
          <MenuItem label="From workspace" onClick={() => setRemoveTarget("workspace")} />
        </MenuItem>;
    }
    return canRemoveFromProject ? <MenuItem label="Remove from project" Icon={DeleteIcon} onClick={() => setRemoveTarget("project")} /> : <MenuItem label="Remove from workspace" Icon={DeleteIcon} onClick={() => setRemoveTarget("workspace")} />;
  }
}
/** Asks to confirm removing a member from the workspace and removes them */
function RemoveFromWorkspaceDialog({
  companyId,
  member,
  onClose
}) {
  const dispatch = useAppDispatch();
  const {
    openToast
  } = useToast();
  const {
    trackEvent
  } = useTrackEvent();
  const companyName = useAppSelector(selectedSdbCompanyNameSelector);
  const refreshSeatUsage = useRefreshSeatUsage();
  const {
    isRemovingCompanyMember
  } = useAppSelector(fetchingMembersFlagsSelector);
  async function removeFromWorkspace() {
    trackEvent({
      name: WorkspaceMemberEvents.removeMember,
      props: {
        dataType: TableType.projectMembers,
        eventLocation: LOCATION,
        removeTarget: "workspace"
      }
    });
    try {
      await dispatch(removeMemberFromCompany({
        companyId,
        member
      })).unwrap();
      refreshSeatUsage();
      openToast({
        title: "Member removed from workspace",
        message: <RemoveMemberSuccessDescription member={member} subjectType="workspace" entityName={companyName ?? ""} />
      });
    } finally {
      onClose();
    }
  }
  return <Dialog title="Remove from workspace" confirmText="Remove" open onConfirm={removeFromWorkspace} onCancel={onClose} showSpinner={isRemovingCompanyMember}>
      <Text variant="bodyM" sx={{
      marginBottom: SPACE_ELEMENTS_OF_MODAL
    }}>
        When removing member from workspace they will no longer be able to
        access this workspace. You can always invite this member again.
      </Text>

      <UserDisplay member={member} />
    </Dialog>;
}